import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    TextField,
    FormControlLabel,
    Radio,
    RadioGroup,
    Button,
    FormControl,
    FormLabel,
    Snackbar,
    Alert,
    CircularProgress,
    Select,
    MenuItem,
    Checkbox,
    Chip,
    Divider,
    Paper,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import {
  decryptText,
  decryptAppId,
  encryptText,
  encryptAppId
} from "../../../utils/encryption";
import PageTitle from '../../../components/PageTitle';
import useAssessmentTracking from '../../../hooks/useAssessmentTracking';
import { LocationWarningDialog, HourlyAttendanceDialog } from '../../../components/AssessmentTrackingDialogs';
import LaboratoryService from '../../../services/laboratoryService';
import PTProviderService from '../../../services/ptProviderService';
import DoApiService from "../../../services/DoApiService";
import s3Utils from '../../../utils/s3Utils';
import AssessorDetailsView from "../../JD/application/AssessorDetailsView";


const NAF6FormNew = () => {
  const location = useLocation();
  
    const getOrgIdFromQuery = () => {
      const searchParams = new URLSearchParams(location.search);
      const encryptedOrgId = searchParams.get("orgId");
      if (encryptedOrgId) {
        try {
          const decryptedOrgId = decryptText(encryptedOrgId);
          return decryptedOrgId;
        } catch (error) {
          return encryptedOrgId;
        }
      }
      return null;
    };
  
    // Function to get appId from URL query parameters with decryption
    const getAppIdFromQuery = () => {
      const searchParams = new URLSearchParams(location.search);
      const encryptedAppId = searchParams.get("appId");
      if (encryptedAppId) {
        try {
          const decryptedAppId = decryptAppId(encryptedAppId);
          return decryptedAppId;
        } catch (error) {
          return encryptedAppId;
        }
      }
      return null;
    };
  
    // Function to get assessmentId from URL query parameters with decryption
    const getAssessmentIdFromQuery = () => {
      const searchParams = new URLSearchParams(location.search);
      const encryptedAssessmentId = searchParams.get("assessmentId");
      if (encryptedAssessmentId) {
        try {
          const decryptedAssessmentId = decryptText(encryptedAssessmentId);
          return decryptedAssessmentId;
        } catch (error) {
          return encryptedAssessmentId;
        }
      }
      return null;
    };
  
    // Function to get assessorId from URL query parameters with decryption
    const getAssessorIdFromQuery = () => {
      const searchParams = new URLSearchParams(location.search);
      const encryptedAssessorId = searchParams.get("assessorId");
      if (encryptedAssessorId) {
        try {
          const decryptedAssessorId = decryptText(encryptedAssessorId);
          return decryptedAssessorId;
        } catch (error) {
          return encryptedAssessorId;
        }
      }
      return null;
    };

    // Function to get labTypeId from URL query parameters with decryption
    const getLabTypeIdFromQuery = () => {
      const searchParams = new URLSearchParams(location.search);
      const encryptedLabTypeId = searchParams.get("labTypeId");
      if (encryptedLabTypeId) {
        try {
          const decryptedLabTypeId = decryptText(encryptedLabTypeId);
          return decryptedLabTypeId;
        } catch (error) {
          return encryptedLabTypeId;
        }
      }
      return null;
    };
  
    const getOrgId = () => {
      const queryOrgId = getOrgIdFromQuery();
      if (queryOrgId) {
        return queryOrgId;
      }
      return null;
    };
  
    const getAppId = () => {
      const queryAppId = getAppIdFromQuery();
      if (queryAppId) {
        return queryAppId;
      }
      return null;
    };
  
    const getAssessmentId = () => {
      const queryAssessmentId = getAssessmentIdFromQuery();
      if (queryAssessmentId) {
        return queryAssessmentId;
      }
      return null;
    };
  
    const getAssessorId = () => {
      const queryAssessorId = getAssessorIdFromQuery();
      if (queryAssessorId) {
        return queryAssessorId;
      }
      return null;
    };
  
    const getLabTypeId = () => {
      const queryLabTypeId = getLabTypeIdFromQuery();
      if (queryLabTypeId) {
        return queryLabTypeId;
      }
      return null;
    };

    const [orgId, setOrgId] = useState(getOrgId());
    const [appId, setAppId] = useState(getAppId());
    const [assessmentId, setAssessmentId] = useState(getAssessmentId());
    const [assessorId, setAssessorId] = useState(getAssessorId());
    const [labTypeId, setLabTypeId] = useState(getLabTypeId());
    const [naf6Id, setNaf6Id] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({
      open: false,
      message: '',
      severity: 'success',
    });
    const [questions, setQuestions] = useState([]);
    const [questionAnswers, setQuestionAnswers] = useState({});
    const [questionRemarks, setQuestionRemarks] = useState({});
    const [labDetails, setLabDetails] = useState(null);
    const [labDetailsDotnet, setLabDetailsDotnet] = useState(null);
    const [loadedNaf6Data, setLoadedNaf6Data] = useState(null);
    const [cabDetails, setCabDetails] = useState(null);
    const [ptData, setPtData] = useState([]);
    const [ptDataPopupOpen, setPtDataPopupOpen] = useState(false);
    const [ptDataLoading, setPtDataLoading] = useState(false);
    const [ilcData, setIlcData] = useState([]);
    const [ilcDataPopupOpen, setIlcDataPopupOpen] = useState(false);
    const [ilcDataLoading, setIlcDataLoading] = useState(false);
    const [negativeOptionDialogOpen, setNegativeOptionDialogOpen] = useState(false);
    const [pendingNegativeSelection, setPendingNegativeSelection] = useState(null);
    const [assessorDialogOpen, setAssessorDialogOpen] = useState(false);
    const [assessorsData, setAssessorsData] = useState([]);
    
  // Form inputs for Quality Assurance section
  const [formInputs, setFormInputs] = useState({
    trainedAuditor: '',
    independenceOfActivity: '',
    ncsClosed: '',
    frequencyOfAudit: '',
    allPointsCovered: '',
    conductedWithinYear: '',
    ptAvailability: '',
    ptNotAvailableReason: '',
    ptCoverageOfScope: '',
    ptOutlier: '',
    ptOutlierRemarks: '',
    ilcCoverageOfScope: '',
    ilcOutlier: '',
    ilcOutlierRemarks: '',
    otherQualityMeasures: '',
    auditComments: '',
    managementComments: ''
  });

  // Assessment tracking hook for location and hourly attendance
  const {
    locationWarningOpen,
    distanceMessage,
    attendanceCheckDialogOpen,
    attendanceCheckMessage,
    setLocationWarningOpen,
    setAttendanceCheckDialogOpen,
    checkHourlyAttendance,
  } = useAssessmentTracking({
    assessorId,
    appId,
    orgId,
  });
  const defaultNCDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
  const [formData, setFormData] = useState({
    // General Information
    cabName: '',
    standard: '',
    labTypeId: '',
    labType: '',
    assessmentTypeId: '',
    assessmentType: '',
    facility: '',
    dateOfEarlierVisit: '',
    nonConformities: '',
    laboratoryTypeSize: '',
    legalIdentity: '',
    operation: '',
    accreditationStatus: '',
    disciplines: '',
    averageDailyWorkload: '',
    scfs: '',
    homeHospitalCollection: '',
    compliance: {
      nabl133: '',
      nabl142: '',
      nabl163: '',
      nabl112a: '',
      nabl135: ''
    },
    complianceRemarks: {
      nabl133: '',
      nabl142: '',
      nabl163: '',
      nabl112a: '',
      nabl135: ''
    },
    ncClause4: 0,
    ncClause5: 0,
    ncClause6: 0,
    ncClause7: 0,
    ncClause8: 0,
    numberOfNCs: '',
    allNCsClosedDate: defaultNCDate,
    recommendForAccreditation: '',
    correctiveActionsDate: '',
    assessmentSummary: '',
    recommendation: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleComplianceChange = (standard, value) => {
    setFormData(prev => ({
      ...prev,
      compliance: {
        ...prev.compliance,
        [standard]: value
      }
    }));
  };

  const handleComplianceRemarkChange = (standard, value) => {
    setFormData(prev => ({
      ...prev,
      complianceRemarks: {
        ...prev.complianceRemarks,
        [standard]: value
      }
    }));
  };

  // Handler for Quality Assurance form inputs
  const handleQAInputChange = (field) => (event) => {
    setFormInputs(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Helper function to format date range
  const formatQualityAssuranceDate = (fromDate, toDate) => {
    const formatSingleDate = (dateString) => {
      if (!dateString) return "Not specified";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      } catch (error) {
        return "Invalid date";
      }
    };

    const from = formatSingleDate(fromDate);
    const to = formatSingleDate(toDate);
    if (from === "Not specified" && to === "Not specified") {
      return "Not specified";
    }
    return `${from} to ${to}`;
  };

  // Handler functions for viewing documents and data
  const handleViewInternalAuditDocument = async () => {
    if (!cabDetails?.InternalAuditDocument) {
      showSnackbar('Internal Audit Document not found', 'error');
      return;
    }
    try {
      setLoading(true);
      await s3Utils.handleFileViewWithS3(cabDetails.InternalAuditDocument, {
        showError: (msg) => showSnackbar(msg, 'error'),
        showLoading: () => setLoading(true),
        hideLoading: () => setLoading(false),
      });
    } catch (error) {
      console.error('Error opening Internal Audit Document:', error);
      showSnackbar('Failed to open Internal Audit Document', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewManagementReviewDocument = async () => {
    if (!cabDetails?.ManagementReviewDocument) {
      showSnackbar('Management Review Document not found', 'error');
      return;
    }
    try {
      setLoading(true);
      await s3Utils.handleFileViewWithS3(cabDetails.ManagementReviewDocument, {
        showError: (msg) => showSnackbar(msg, 'error'),
        showLoading: () => setLoading(true),
        hideLoading: () => setLoading(false),
      });
    } catch (error) {
      console.error('Error opening Management Review Document:', error);
      showSnackbar('Failed to open Management Review Document', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewQMSDocument = async () => {
    if (!cabDetails?.QMSFile) {
      showSnackbar('QMS Document not found', 'error');
      return;
    }
    try {
      setLoading(true);
      await s3Utils.handleFileViewWithS3(cabDetails.QMSFile, {
        showError: (msg) => showSnackbar(msg, 'error'),
        showLoading: () => setLoading(true),
        hideLoading: () => setLoading(false),
      });
    } catch (error) {
      console.error('Error opening QMS Document:', error);
      showSnackbar('Failed to open QMS Document', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPTData = async () => {
    setPtDataPopupOpen(true);
    if (ptData.length > 0) return; // Data already loaded
    
    try {
      setPtDataLoading(true);
      const response = await PTProviderService.getPTProviderData(appId, orgId);
      if (response.success) {
        const filteredPTData = (response.data || []).filter(
          (item) =>
            item.participationIn === "PT" ||
            item.participationIn === "PT/EQA (NABL Accredited)" ||
            item.participationIn === "PT/EQA (Others)" ||
            item.participationIn?.toLowerCase().includes("pt"),
        );
        setPtData(filteredPTData);
      }
    } catch (error) {
      console.error('Error loading PT data:', error);
      showSnackbar('Failed to load PT data', 'error');
    } finally {
      setPtDataLoading(false);
    }
  };

  const handleViewILCData = async () => {
    setIlcDataPopupOpen(true);
    if (ilcData.length > 0) return; // Data already loaded
    
    try {
      setIlcDataLoading(true);
      const response = await PTProviderService.getPTProviderData(appId, orgId);
      if (response.success) {
        const filteredILCData = (response.data || []).filter(
          (item) => item.participationIn === "ILC",
        );
        setIlcData(filteredILCData);
      }
    } catch (error) {
      console.error('Error loading ILC data:', error);
      showSnackbar('Failed to load ILC data', 'error');
    } finally {
      setIlcDataLoading(false);
    }
  };

  const getFieldPlaceholder = (text) => text;
  const getNCsByClause = () => [];
  const colors = {};
  const WorkingSpaceDocument = () => null;
  const handleOpenLightbox = () => {};
  const fieldsDisabled = false; // Fields are now editable

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Section name to DB field mapping
  const sectionFieldMapping = {
    'Overall Compliance': 'overallCompliance',
    'Pre-Examination Processes': 'preExaminationProcesses',
    'Facilities & Environmental conditions': 'infrastructure',
    'Equipment': 'equipment',
    'Laboratory personnel': 'laboratoryStaff',
    'Laboratory Personnel': 'laboratoryStaff',
    'Authorized Signatories': 'authorizedSignatories',
    'Ensuring validity of examination results (IQC)': 'IQC',
    'Ensuring of validity of examination results (Internal Quality Control)': 'IQC',
    'EQA (External Quality Assurance)': 'EQAS',
    'Proficiency Testing/ Inter Laboratory Comparison': 'EQAS',
    'Reporting of Results': 'reportingResults',
    'Quality Indicators': 'qualityIndicators',
    'Complaints & Feedback': 'complaintsFeedback',
    'Compliance to NABL policies': 'nonConformanceHandling',
    'Risk Management': 'riskManagement',
    'Control of data & information management (LIS)': 'LISAutoVerification',
    'Control of data & information management': 'LISAutoVerification',
    'Lab Safety & Waste Management': 'labSafety',
    'Areas of Strength': 'areasStrength',
    'Areas for Improvement': 'areasforImprovement',
    };

  // Fetch existing NAF6 data
  useEffect(() => {
    const fetchLabDetails = async () => {
      if (!appId || !assessmentId) return;

      try {
        const url = `${process.env.REACT_APP_API_URL}/naf6/lab-details/${appId}/${assessmentId}`;
        const response = await axios.get(url);
        
        if (response.data && response.data.success && response.data.data) {
          const data = response.data.data;
          setLabDetails(data);
          
          const cabTypeMap = {
            'Diagnostic': 'Medical Testing Laboratory',
            'Medical Imaging': 'Medical Imaging',
            'Both': 'Medical Testing Laboratory + Medical Imaging'
          };
          
          setFormData(prev => ({
            ...prev,
            legalIdentity: data.LegalEntityName,
            disciplines: data.DisciplineNames,
            laboratoryTypeSize: (cabTypeMap[data.CabType] || data.CabType) + " / " + data.AccreditationCategory,
            cabName: data.CabName,
            standard: (data.LabTypeId===1||data.LabTypeId==2) ? 'ISO/IEC 17025:'+data.Standard : 'ISO 15189:'+data.Standard,
            labTypeId: data.LabTypeId,
            labType: data.LabType,
            assessmentTypeId: data.AssessmentTypeId,
            assessmentType: data.AssessmentType,
            facility: data.Facility,
            ncClause4: data.clause4Count,
            ncClause5: data.clause5Count,
            ncClause6: data.clause6Count,
            ncClause7: data.clause7Count,
            ncClause8: data.clause8Count
          }));
        }
      } catch (error) {
        console.error('Error fetching lab details:', error);
        showSnackbar('Failed to fetch lab details', 'error');
      }
    };

    const fetchQuestions = async () => {
      try {
        const url = `${process.env.REACT_APP_API_URL}/naf6/questions-new/${labTypeId}`;
        const response = await axios.get(url);

        if (response.data && response.data.success) {
          setQuestions(response.data.data || []);
        }
      } catch (error) {
        console.error("Error fetching NAF6 questions:", error);
      }
    };

    const fetchCABDetails = async () => {
      if (!appId) return;
      
      try {
       const response = await DoApiService.getCABDetails({
               appId: parseFloat(appId),
             });
             if (response.success) {
               setCabDetails(response.data);
          console.log('CAB Details loaded:', response.data.data);
        }
      } catch (error) {
        console.error('Error fetching CAB details:', error);
      }
    };

    const fetchLabDetailsDotnet = async () => {
      if (!appId || !assessorId) return;
        try {
          const apiUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Getlabdetails`;
    
          const params = {
            appid: appId,
            orgid: assessorId,
          };
          const response = await axios.get(apiUrl, { params });
          if (response.data && response.data.isstatus) {
            const lab = response.data;
            setLabDetailsDotnet(lab);
          } 
         } catch (error) {
          console.error('Error fetching lab details:', error);
        } 
    };

    const loadAssessorAllotmentData = async () => {
        if (!appId) {
          return;
        }
        try {
          const response = await DoApiService.getAssessorAllotmentDetails9({
            mode: 64,
            appId: parseFloat(appId),
          });
    
          if (response.success) {
            setAssessorsData(response.data || []);
            // Log capacity information for debugging
            response.data?.forEach((assessor, index) => {
              console.log(`Assessor ${index + 1}:`, {
                name: assessor.assessorName || assessor.name,
                id: assessor.allotedToId,
                capacity: assessor.capacity,
                fullData: assessor
              });
            });
          } else {
            throw new Error(response.message || "Failed to load assessor allotment details");
          }
        } catch (err) {
          console.error("Load assessor allotment error:", err);
          setAssessorsData([]);
        } 
      };

    Promise.all([fetchQuestions(), fetchNAF6Data(), fetchLabDetails(), fetchCABDetails(), fetchLabDetailsDotnet(), loadAssessorAllotmentData()]).finally(() => {
      setDataLoading(false);
    });
  }, [appId, assessmentId, assessorId, orgId]);

  const fetchNAF6Data = async () => {
      if (!appId || !assessmentId || !assessorId) return;

      try {
        setDataLoading(true);
        const url = `${process.env.REACT_APP_API_URL}/naf6/data/${appId}/${assessorId}/${assessmentId}`;
        const response = await axios.get(url);

        if (response.data && response.data.success && response.data.data) {
          const data = response.data.data;
          setNaf6Id(data.Id);
          setFormData(prev => ({
            ...prev,
            operation: data.operation || '',
            accreditationStatus: data.accreditationStatus || '',
            averageDailyWorkload: data.dailyWorkLoad || '',
            scfs: data.SCF || '',
            homeHospitalCollection: data.hospitalCollectionService || '',
            compliance: {
              nabl133: data.nabl133Value || '',
              nabl142: data.nabl142Value || '',
              nabl163: data.nabl163Value || '',
              nabl112a: data.nabl112a || '',
              nabl135: data.nabl135 || ''
            },
            complianceRemarks: {
              nabl133: data.nabl133NcStatement || '',
              nabl142: data.nabl142NcStatement || '',
              nabl163: data.nabl163NcStatement || '',
              nabl112a: data.nabl112ANcStatement || '',
              nabl135: data.nabl135NcStatement || ''
            },
            ncClause4: data.ncClause4 || 0,
            ncClause5: data.ncClause5 || 0,
            ncClause6: data.ncClause6 || 0,
            ncClause7: data.ncClause7 || 0,
            ncClause8: data.ncClause8 || 0,
            numberOfNCs: data.numberOfNCs || '',
            allNCsClosedDate: data.allNCsClosedDate || defaultNCDate || '',
            recommendForAccreditation: data.isLabRecommend || '',
            correctiveActionsDate: data.dateOfSubmission || '',
            assessmentSummary: data.assessmentSummary || '',
            recommendation: data.recommendationsOfAssessmentTeam || '',
            nonConformities: data.nonConformities || '',
            dateOfEarlierVisit: data.dates || ''
          }));

          // Parse question answers and remarks from "~" separated values
          // Store temporarily in state to load when questions are available
          const tempLoadedData = {
            answers: {},
            remarks: {}
          };
          
          Object.entries(sectionFieldMapping).forEach(([sectionName, fieldName]) => {
            // Parse answers (option text values separated by ~)
            const answerValue = data[fieldName];
            if (answerValue && answerValue.trim()) {
              tempLoadedData.answers[sectionName] = answerValue.split('~').map(text => text.trim()).filter(text => text);
            }
            
            // Parse remarks
            const remarkField = `${fieldName}Remark`;
            const remarkValue = data[remarkField];
            if (remarkValue && remarkValue.trim()) {
              tempLoadedData.remarks[sectionName] = remarkValue;
            }
          });
          
          setLoadedNaf6Data(tempLoadedData);

          // Populate Quality Assurance Measures fields
          setFormInputs(prev => ({
            ...prev,
            trainedAuditor: data.trainedAuditor || '',
            independenceOfActivity: data.independenceOfActivity || '',
            ncsClosed: data.ncsClosed || '',
            frequencyOfAudit: data.frequencyOfAudit || '',
            allPointsCovered: data.allPointsCovered || '',
            conductedWithinYear: data.conductedWithinYear || '',
            auditComments: data.auditComments || '',
            managementComments: data.managementComments || '',
            ptAvailability: data.ptAvailability || '',
            ptNotAvailableReason: data.ptNotAvailableReason || '',
            ptCoverageOfScope: data.ptCoverageOfScope || '',
            ptOutlier: data.ptOutlier || '',
            ptOutlierRemarks: data.ptOutlierRemarks || '',
            ilcCoverageOfScope: data.ilcCoverageOfScope || '',
            ilcOutlier: data.ilcOutlier || '',
            ilcOutlierRemarks: data.ilcOutlierRemarks || '',
            otherQualityMeasures: data.otherQualityMeasures || ''
          }));
        }
      } catch (error) {
        console.error('Error fetching NAF6 data:', error);
      } finally {
        setDataLoading(false);
      }
    };

  // Populate question answers when questions are loaded
  useEffect(() => {
    if (questions.length > 0 && loadedNaf6Data) {
      const newQuestionAnswers = {};
      const newQuestionRemarks = {};

      // Group questions by section
      const questionsBySection = questions.reduce((acc, q) => {
        if (!acc[q.SectionName]) {
          acc[q.SectionName] = [];
        }
        acc[q.SectionName].push(q);
        return acc;
      }, {});

      // For each section, populate the first question with the loaded answers
      Object.entries(questionsBySection).forEach(([sectionName, sectionQuestions]) => {
        const questionsByQuestionId = sectionQuestions.reduce((acc, q) => {
          if (!acc[q.QuestionId]) {
            acc[q.QuestionId] = [];
          }
          acc[q.QuestionId].push(q);
          return acc;
        }, {});

        const firstQuestionId = Object.keys(questionsByQuestionId)[0];
        if (firstQuestionId) {
          const questionKey = `${sectionName}-${firstQuestionId}`;
          
          // Set answers
          if (loadedNaf6Data.answers[sectionName]) {
            newQuestionAnswers[questionKey] = loadedNaf6Data.answers[sectionName];
          }
          
          // Set remarks
          if (loadedNaf6Data.remarks[sectionName]) {
            newQuestionRemarks[questionKey] = loadedNaf6Data.remarks[sectionName];
          }
        }
      });

      setQuestionAnswers(newQuestionAnswers);
      setQuestionRemarks(newQuestionRemarks);
      
      // Clear loaded data after processing
      setLoadedNaf6Data(null);
    }
  }, [questions, loadedNaf6Data]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!appId || !orgId || !assessorId || !assessmentId) {
      showSnackbar('Missing required parameters', 'error');
      return;
    }
    // Get unique section names from the actual questions data
    const actualSections = [...new Set(questions.map(q => q.SectionName))];
    // Validate that all actual sections have answers
    const missingSections = [];
    actualSections.forEach(sectionName => {
      const hasSectionAnswers = Object.keys(questionAnswers).some(questionKey => {
        if (questionKey.startsWith(sectionName + '-')) {
          const answers = questionAnswers[questionKey] || [];
          return answers.length > 0;
        }
        return false;
      });
      if (!hasSectionAnswers) {
        missingSections.push(sectionName);
      }
    });
    if (missingSections.length > 0) {
      showSnackbar(`Please select options for: ${missingSections.join(', ')}`, 'error');
      return;
    }
    // Validate mandatory fields
    // if (!formData.operation || !formData.accreditationStatus || !formData.averageDailyWorkload || 
    //     !formData.scfs || !formData.homeHospitalCollection) {
    //   showSnackbar('Please fill all mandatory fields', 'error');
    //   return;
    // }

    // Validate Quality Assurance Measures fields
    const qaFields = [
      { field: 'trainedAuditor', label: 'Trained Auditor' },
      { field: 'independenceOfActivity', label: 'Independence of Activity' },
      { field: 'ncsClosed', label: 'NCs Closed' },
      { field: 'frequencyOfAudit', label: 'Frequency of Audit' },
      { field: 'allPointsCovered', label: 'All Points Covered' },
      { field: 'conductedWithinYear', label: 'Conducted Within Year' },
      { field: 'ptAvailability', label: 'PT Availability' },
      { field: 'ptCoverageOfScope', label: 'PT Coverage of Scope' },
      { field: 'ptOutlier', label: 'PT Outlier' },
      { field: 'ilcCoverageOfScope', label: 'ILC Coverage of Scope' },
      { field: 'ilcOutlier', label: 'ILC Outlier' }
    ];

    const missingQAFields = qaFields.filter(({ field }) => !formInputs[field]);
    if (missingQAFields.length > 0) {
      const missingLabels = missingQAFields.map(({ label }) => label).join(', ');
      showSnackbar(`Please fill Quality Assurance fields: ${missingLabels}`, 'error');
      return;
    }

    // Validate PT Outlier Remarks if PT Outlier is Yes
    if (formInputs.ptOutlier === 'Yes' && !formInputs.ptOutlierRemarks) {
      showSnackbar('Please provide PT Outlier Remarks', 'error');
      return;
    }

    // Validate ILC Outlier Remarks if ILC Outlier is Yes
    if (formInputs.ilcOutlier === 'Yes' && !formInputs.ilcOutlierRemarks) {
      showSnackbar('Please provide ILC Outlier Remarks', 'error');
      return;
    }
    // if (!formData.recommendForAccreditation) {
    //   showSnackbar('Please select lab recommendation for accreditation', 'error');
    //   return;
    // }
    // if (formData.recommendForAccreditation === 'Yes' && !formData.correctiveActionsDate) {
    //   showSnackbar('Please provide corrective actions date', 'error');
    //   return;
    // }
    if(!formData.recommendation) {
      showSnackbar('Please provide recommendations of assessment team', 'error');
      return;
    }
    if(formData.assessmentTypeId!==1) {
      if (!formData.nonConformities) {
        showSnackbar('Please select Non-Conformities during earlier onsite assessment have/ have not been discharged ', 'error');
        return;
      }
    }
    setLoading(true);
    try {
      // Prepare question answers and remarks based on section mapping
      const questionData = {};
      // Initialize all fields to empty string
      Object.values(sectionFieldMapping).forEach(fieldName => {
        questionData[fieldName] = '';
        questionData[`${fieldName}Remark`] = '';
      });
      // Group answers by section name from actual questionAnswers
      Object.entries(questionAnswers).forEach(([questionKey, answers]) => {
        // Extract section name from questionKey (format: "SectionName-QuestionId")
        // Split from the last hyphen since section names may contain hyphens
        const lastHyphenIndex = questionKey.lastIndexOf('-');
        const sectionName = questionKey.substring(0, lastHyphenIndex);
        
        // Get the field name from mapping
        const fieldName = sectionFieldMapping[sectionName];
        
        if (fieldName) {
          // Append answers to existing field value
          const existingValue = questionData[fieldName];
          const newAnswers = answers.join('~');
          
          if (existingValue && newAnswers) {
            questionData[fieldName] = existingValue + '~' + newAnswers;
          } else if (newAnswers) {
            questionData[fieldName] = newAnswers;
          }
          
          // Append remarks
          const remark = questionRemarks[questionKey];
          if (remark && remark.trim()) {
            const remarkField = `${fieldName}Remark`;
            const existingRemark = questionData[remarkField];
            
            if (existingRemark) {
              questionData[remarkField] = existingRemark + '\n' + remark.trim();
            } else {
              questionData[remarkField] = remark.trim();
            }
          }
        } else {
          // Section not found in mapping - skip silently
        }
      });

      const payload = {
        appId: String(appId),
        orgId: String(orgId),
        assessorId: String(assessorId),
        assessmentId: String(assessmentId),
        // General Information
        dates: formData.dateOfEarlierVisit,
        nonConformities: formData.nonConformities,
        operation: formData.operation,
        accreditationStatus: formData.accreditationStatus,
        dailyWorkLoad: formData.averageDailyWorkload,
        SCF: formData.scfs,
        hospitalCollectionService: formData.homeHospitalCollection,
        nabl133Value: formData.compliance.nabl133,
        nabl133NcStatement: formData.complianceRemarks.nabl133,
        nabl142Value: formData.compliance.nabl142,
        nabl142NcStatement: formData.complianceRemarks.nabl142,
        nabl163Value: formData.compliance.nabl163,
        nabl163NcStatement: formData.complianceRemarks.nabl163,
        nabl112a: formData.compliance.nabl112a,
        nabl112ANcStatement: formData.complianceRemarks.nabl112a,
        nabl135: formData.compliance.nabl135,
        nabl135NcStatement: formData.complianceRemarks.nabl135,
        ncClause4: formData.ncClause4,
        ncClause5: formData.ncClause5,
        ncClause6: formData.ncClause6,
        ncClause7: formData.ncClause7,
        ncClause8: formData.ncClause8,
        numberOfNCs: (parseInt(formData.ncClause4) || 0) +
                     (parseInt(formData.ncClause5) || 0) +
                     (parseInt(formData.ncClause6) || 0) +
                     (parseInt(formData.ncClause7) || 0) +
                     (parseInt(formData.ncClause8) || 0),
        allNCsClosedDate: formData.allNCsClosedDate,
        isLabRecommend: formData.recommendForAccreditation,
        dateOfSubmission: formData.recommendForAccreditation === 'Yes' ? formData.correctiveActionsDate : '',
        Recommended17: '',
        NotRecommended17: '',
        Remarks: '',
        assessmentSummary: formData.assessmentSummary,
        recommendationsOfAssessmentTeam: formData.recommendation,
        // Add all question data fields
        ...questionData,
        // Quality Assurance Measures
        trainedAuditor: formInputs.trainedAuditor,
        independenceOfActivity: formInputs.independenceOfActivity,
        ncsClosed: formInputs.ncsClosed,
        frequencyOfAudit: formInputs.frequencyOfAudit,
        allPointsCovered: formInputs.allPointsCovered,
        conductedWithinYear: formInputs.conductedWithinYear,
        auditComments: formInputs.auditComments,
        managementComments: formInputs.managementComments,
        ptAvailability: formInputs.ptAvailability,
        ptNotAvailableReason: formInputs.ptNotAvailableReason,  
        ptCoverageOfScope: formInputs.ptCoverageOfScope,
        ptOutlier: formInputs.ptOutlier,
        ptOutlierRemarks: formInputs.ptOutlierRemarks,
        ilcCoverageOfScope: formInputs.ilcCoverageOfScope,
        ilcOutlier: formInputs.ilcOutlier,
        ilcOutlierRemarks: formInputs.ilcOutlierRemarks,
        otherQualityMeasures: formInputs.otherQualityMeasures,
        insertedBy: localStorage.getItem('orgId') || '0',
        updatedBy: localStorage.getItem('orgId') || '0'
      };

      let response;
      if (naf6Id) {
        // Update existing NAF6
        const url = `${process.env.REACT_APP_API_URL}/naf6/update`;
        response = await axios.put(url, payload);
      } else {
        // Create new NAF6
        const url = `${process.env.REACT_APP_API_URL}/naf6/submit`;
        response = await axios.post(url, payload);
      }

      if (response.data && response.data.success) {
        showSnackbar(naf6Id ? 'NAF6 updated successfully!' : 'NAF6 saved successfully!', 'success');
        await fetchNAF6Data();
      } else {
        throw new Error(response.data.message || 'Failed to save NAF6');
      }
    } catch (error) {
      console.error('Error submitting NAF6:', error);
      showSnackbar(
        error.response?.data?.message || error.message || 'Error saving NAF6',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageTitle title="NABL | NAF-6" />
      <Box sx={{ p: 1, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
        {dataLoading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Header */}
        <Paper
          elevation={2}
          sx={{
            p: 2,
            mb: 2,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: "bold", color: "white", textAlign: "center" }}
          >
            NAF-6 Assessment Summary
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: "rgba(255,255,255,0.9)", textAlign: "center", mt: 1 }}
          >
            National Accreditation Board for Testing and Calibration
            Laboratories
          </Typography>
        </Paper>

        {/* Main Form Card */}
        <Card sx={{ mb: 2, boxShadow: 4, borderRadius: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <form onSubmit={handleSubmit}>
              {/* General Information Section */}
              <Card
                sx={{
                  mb: 2,
                  boxShadow: 2,
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        color: "primary.main",
                      }}
                    >
                      📋 Laboratory Information
                    </Typography>

                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setAssessorDialogOpen(true)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: "8px",
                      }}
                    >
                      View Assessment Team / Capacity
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 600,
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Name of the Laboratory
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 500, color: "text.primary" }}
                        >
                          {formData.cabName || "—"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 600,
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Field
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 500, color: "text.primary" }}
                        >
                          {formData.labType || "—"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 600,
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Standard
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 500, color: "text.primary" }}
                        >
                          {formData.standard || "—"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 600,
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Date(s) of Assessment
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 500, color: "text.primary" }}
                        >
                          {labDetailsDotnet
                            ? `${labDetailsDotnet.assessmentDateFrom} to ${labDetailsDotnet.assessmentDateTo}`
                            : "—"}
                        </Typography>
                      </Box>
                    </Grid>

                    {cabDetails && cabDetails.labTypeId === 3 && (
                      <Grid item xs={12} md={6}>
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 600,
                              display: "block",
                              mb: 0.5,
                            }}
                          >
                            Type & Size of Laboratory
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, color: "text.primary" }}
                          >
                            {formData.laboratoryTypeSize || "—"}
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 600,
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Legal Identity
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 500, color: "text.primary" }}
                        >
                          {formData.legalIdentity || "—"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 600,
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Discipline(s)
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 500, color: "text.primary" }}
                        >
                          {formData.disciplines || "—"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 600,
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Type of Assessment
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 500, color: "text.primary" }}
                        >
                          {formData.assessmentType || "—"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 600,
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Facility
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 500, color: "text.primary" }}
                        >
                          {formData.facility || "—"}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              <Card
                sx={{
                  mb: 2,
                  boxShadow: 2,
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        color: "primary.main",
                        flex: 1,
                      }}
                    >
                      📋 General Information
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    {formData.assessmentTypeId !== 1 && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Date of Earlier Visit"
                            type="date"
                            value={formData.dateOfEarlierVisit}
                            onChange={(e) =>
                              handleInputChange(
                                "dateOfEarlierVisit",
                                e.target.value,
                              )
                            }
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl component="fieldset" fullWidth required>
                            <FormLabel
                              component="legend"
                              sx={{ fontWeight: "bold", mb: 2 }}
                            >
                              Non-Conformities during earlier onsite assessment
                              have/ have not been discharged.
                            </FormLabel>
                            <RadioGroup
                              row
                              value={formData.nonConformities}
                              onChange={(e) =>
                                handleInputChange(
                                  "nonConformities",
                                  e.target.value,
                                )
                              }
                            >
                              <FormControlLabel
                                value="Yes"
                                control={<Radio />}
                                label="Yes"
                              />
                              <FormControlLabel
                                value="No"
                                control={<Radio />}
                                label="No"
                              />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Operation"
                        value={formData.operation}
                        onChange={(e) =>
                          handleInputChange("operation", e.target.value)
                        }
                        margin="normal"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Accreditation Status (ongoing, previous & others)"
                        value={formData.accreditationStatus}
                        onChange={(e) =>
                          handleInputChange(
                            "accreditationStatus",
                            e.target.value,
                          )
                        }
                        margin="normal"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Average Daily Work load"
                        value={formData.averageDailyWorkload}
                        onChange={(e) =>
                          handleInputChange(
                            "averageDailyWorkload",
                            e.target.value,
                          )
                        }
                        margin="normal"
                        required
                      />
                    </Grid>
                    {formData.labTypeId === 3 && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="SCFs (Sample Collection Centre/Facility)"
                          value={formData.scfs}
                          onChange={(e) =>
                            handleInputChange("scfs", e.target.value)
                          }
                          margin="normal"
                          required
                        />
                      </Grid>
                    )}
                    {formData.labTypeId === 3 && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Home & Hospital Collection Services"
                        value={formData.homeHospitalCollection}
                        onChange={(e) =>
                          handleInputChange(
                            "homeHospitalCollection",
                            e.target.value,
                          )
                        }
                        margin="normal"
                        required
                      />
                    </Grid>
                    )}
                    {(formData.labTypeId === 1 || formData.labTypeId === 2) && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Site Facility"
                        value={formData.homeHospitalCollection}
                        onChange={(e) =>
                          handleInputChange(
                            "homeHospitalCollection",
                            e.target.value,
                          )
                        }
                        margin="normal"
                        required
                      />
                    </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
              {/* NAF6 Questions Section */}
              {questions.length > 0 &&
                (() => {
                  // Group questions by SectionName
                  const questionsBySection = questions.reduce((acc, q) => {
                    if (!acc[q.SectionName]) {
                      acc[q.SectionName] = [];
                    }
                    acc[q.SectionName].push(q);
                    return acc;
                  }, {});

                  return Object.entries(questionsBySection).map(
                    ([sectionName, sectionQuestions], index) => {
                      // Group by QuestionId within section
                      const questionsByQuestionId = sectionQuestions.reduce(
                        (acc, q) => {
                          if (!acc[q.QuestionId]) {
                            acc[q.QuestionId] = [];
                          }
                          acc[q.QuestionId].push(q);
                          return acc;
                        },
                        {},
                      );

                      return (
                        <Card
                          key={sectionName}
                          sx={{
                            mb: 2,
                            boxShadow: 2,
                            borderRadius: 2,
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 2,
                              }}
                            >
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: "bold",
                                  color: "primary.main",
                                  flex: 1,
                                }}
                              >
                                📊 {index + 1}. {sectionName}
                              </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            {Object.entries(questionsByQuestionId).map(
                              ([questionId, questionOptions]) => {
                                // Group options by OptionType
                                const optionsByType = questionOptions.reduce(
                                  (acc, opt) => {
                                    if (!acc[opt.OptionType]) {
                                      acc[opt.OptionType] = [];
                                    }
                                    acc[opt.OptionType].push(opt);
                                    return acc;
                                  },
                                  {},
                                );

                                const questionKey = `${sectionName}-${questionId}`;

                                return (
                                  <Card
                                    key={questionId}
                                    variant="outlined"
                                    sx={{
                                      mb: 2,
                                      p: 2,
                                      backgroundColor: "#fafafa",
                                    }}
                                  >
                                    <Grid container spacing={2}>
                                      <Grid item xs={12}>
                                        <Select
                                          fullWidth
                                          multiple
                                          displayEmpty
                                          required
                                          value={
                                            questionAnswers[questionKey] || []
                                          }
                                          onChange={(e) => {
                                            const selectedTexts =
                                              e.target.value;

                                            // Check if any newly selected option is of type "Negative"
                                            const previouslySelected =
                                              questionAnswers[questionKey] ||
                                              [];
                                            const newlySelected =
                                              selectedTexts.filter(
                                                (text) =>
                                                  !previouslySelected.includes(
                                                    text,
                                                  ),
                                              );

                                            // Find newly selected negative options
                                            const newlySelectedNegativeOptions =
                                              newlySelected.filter(
                                                (optionText) => {
                                                  const opt =
                                                    questionOptions.find(
                                                      (o) =>
                                                        o.OptionText ===
                                                        optionText,
                                                    );
                                                  return (
                                                    opt &&
                                                    opt.OptionType ===
                                                      "Negative"
                                                  );
                                                },
                                              );

                                            if (
                                              newlySelectedNegativeOptions.length >
                                              0
                                            ) {
                                              // Store pending selection and show confirmation dialog
                                              setPendingNegativeSelection({
                                                questionKey,
                                                selectedTexts,
                                                newlySelectedNegative:
                                                  newlySelectedNegativeOptions,
                                              });
                                              setNegativeOptionDialogOpen(true);
                                            } else {
                                              // No negative option, proceed normally
                                              setQuestionAnswers({
                                                ...questionAnswers,
                                                [questionKey]: selectedTexts,
                                              });
                                            }
                                          }}
                                          renderValue={(selected) => {
                                            if (
                                              !selected ||
                                              selected.length === 0
                                            ) {
                                              return (
                                                <em style={{ color: "#999" }}>
                                                  --Select Options--
                                                </em>
                                              );
                                            }
                                            return (
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  flexWrap: "wrap",
                                                  gap: 0.5,
                                                }}
                                              >
                                                {selected.map(
                                                  (optionText, idx) => {
                                                    // Find the option to get its type
                                                    const opt =
                                                      questionOptions.find(
                                                        (o) =>
                                                          o.OptionText ===
                                                          optionText,
                                                      );
                                                    let chipColor = "default";
                                                    if (opt) {
                                                      if (
                                                        opt.OptionType ===
                                                        "Positive"
                                                      )
                                                        chipColor = "success";
                                                      else if (
                                                        opt.OptionType ===
                                                        "Negative"
                                                      )
                                                        chipColor = "error";
                                                      else if (
                                                        opt.OptionType ===
                                                        "Neutral"
                                                      )
                                                        chipColor = "warning";
                                                      else if (
                                                        opt.OptionType ===
                                                        "Improvement"
                                                      )
                                                        chipColor = "info";
                                                      else if (
                                                        opt.OptionType ===
                                                        "Conditional"
                                                      )
                                                        chipColor = "secondary";
                                                    }

                                                    return (
                                                      <Chip
                                                        key={idx}
                                                        label={optionText}
                                                        size="small"
                                                        color={chipColor}
                                                        sx={{
                                                          maxWidth: "100%",
                                                        }}
                                                      />
                                                    );
                                                  },
                                                )}
                                              </Box>
                                            );
                                          }}
                                          sx={{
                                            backgroundColor: "white",
                                          }}
                                        >
                                          {/* Positive Options */}
                                          {optionsByType["Positive"] &&
                                            optionsByType["Positive"].map(
                                              (option) => (
                                                <MenuItem
                                                  key={option.Id}
                                                  value={option.OptionText}
                                                >
                                                  <Checkbox
                                                    checked={
                                                      (
                                                        questionAnswers[
                                                          questionKey
                                                        ] || []
                                                      ).indexOf(
                                                        option.OptionText,
                                                      ) > -1
                                                    }
                                                  />
                                                  <Typography>
                                                    {option.OptionText}
                                                  </Typography>
                                                </MenuItem>
                                              ),
                                            )}

                                          {/* Neutral Options */}
                                          {optionsByType["Neutral"] &&
                                            optionsByType["Neutral"].map(
                                              (option) => (
                                                <MenuItem
                                                  key={option.Id}
                                                  value={option.OptionText}
                                                >
                                                  <Checkbox
                                                    checked={
                                                      (
                                                        questionAnswers[
                                                          questionKey
                                                        ] || []
                                                      ).indexOf(
                                                        option.OptionText,
                                                      ) > -1
                                                    }
                                                  />
                                                  <Typography>
                                                    {option.OptionText}
                                                  </Typography>
                                                </MenuItem>
                                              ),
                                            )}

                                          {/* Improvement Options */}
                                          {optionsByType["Improvement"] &&
                                            optionsByType["Improvement"].map(
                                              (option) => (
                                                <MenuItem
                                                  key={option.Id}
                                                  value={option.OptionText}
                                                >
                                                  <Checkbox
                                                    checked={
                                                      (
                                                        questionAnswers[
                                                          questionKey
                                                        ] || []
                                                      ).indexOf(
                                                        option.OptionText,
                                                      ) > -1
                                                    }
                                                  />
                                                  <Typography>
                                                    {option.OptionText}
                                                  </Typography>
                                                </MenuItem>
                                              ),
                                            )}

                                          {/* Conditional Options */}
                                          {optionsByType["Conditional"] &&
                                            optionsByType["Conditional"].map(
                                              (option) => (
                                                <MenuItem
                                                  key={option.Id}
                                                  value={option.OptionText}
                                                >
                                                  <Checkbox
                                                    checked={
                                                      (
                                                        questionAnswers[
                                                          questionKey
                                                        ] || []
                                                      ).indexOf(
                                                        option.OptionText,
                                                      ) > -1
                                                    }
                                                  />
                                                  <Typography>
                                                    {option.OptionText}
                                                  </Typography>
                                                </MenuItem>
                                              ),
                                            )}

                                          {/* Negative Options */}
                                          {optionsByType["Negative"] &&
                                            optionsByType["Negative"].map(
                                              (option) => (
                                                <MenuItem
                                                  key={option.Id}
                                                  value={option.OptionText}
                                                >
                                                  <Checkbox
                                                    checked={
                                                      (
                                                        questionAnswers[
                                                          questionKey
                                                        ] || []
                                                      ).indexOf(
                                                        option.OptionText,
                                                      ) > -1
                                                    }
                                                  />
                                                  <Typography>
                                                    {option.OptionText}
                                                  </Typography>
                                                </MenuItem>
                                              ),
                                            )}
                                        </Select>
                                      </Grid>

                                      <Grid item xs={12}>
                                        <TextField
                                          fullWidth
                                          multiline
                                          rows={2}
                                          label="Remarks"
                                          placeholder="Enter remarks for this question..."
                                          value={
                                            questionRemarks[questionKey] || ""
                                          }
                                          onChange={(e) =>
                                            setQuestionRemarks({
                                              ...questionRemarks,
                                              [questionKey]: e.target.value,
                                            })
                                          }
                                          sx={{ backgroundColor: "white" }}
                                        />
                                      </Grid>
                                    </Grid>
                                  </Card>
                                );
                              },
                            )}
                          </CardContent>
                        </Card>
                      );
                    },
                  );
                })()}

              {/* Compliance to other NABL documents */}
              <Card
                sx={{
                  mb: 2,
                  boxShadow: 2,
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        color: "primary.main",
                        flex: 1,
                      }}
                    >
                      📊 {labTypeId === '2' ? '15.' : '17.'} Compliance to other NABL documents
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={3}>
                            <FormLabel sx={{ fontWeight: "bold" }}>
                              (a) NABL 133*
                            </FormLabel>
                          </Grid>
                          <Grid item xs={12} md={9}>
                            <RadioGroup
                              row
                              value={formData.compliance.nabl133}
                              onChange={(e) =>
                                handleComplianceChange(
                                  "nabl133",
                                  e.target.value,
                                )
                              }
                            >
                              <FormControlLabel
                                value="Compliance"
                                control={<Radio />}
                                label="Compliance"
                              />
                              <FormControlLabel
                                value="Non Compliance"
                                control={<Radio />}
                                label="Non Compliance"
                              />
                            </RadioGroup>
                            {formData.compliance.nabl133 ===
                              "Non Compliance" && (
                              <TextField
                                fullWidth
                                label="Remarks for Non-Compliance"
                                value={formData.complianceRemarks.nabl133}
                                onChange={(e) =>
                                  handleComplianceRemarkChange(
                                    "nabl133",
                                    e.target.value,
                                  )
                                }
                                margin="normal"
                                multiline
                                rows={2}
                                required
                                placeholder="Please provide details for non-compliance..."
                              />
                            )}
                          </Grid>
                        </Grid>
                      </Card>

                      <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={3}>
                            <FormLabel sx={{ fontWeight: "bold" }}>
                              (b) NABL 142*
                            </FormLabel>
                          </Grid>
                          <Grid item xs={12} md={9}>
                            <RadioGroup
                              row
                              value={formData.compliance.nabl142}
                              onChange={(e) =>
                                handleComplianceChange(
                                  "nabl142",
                                  e.target.value,
                                )
                              }
                            >
                              <FormControlLabel
                                value="Compliance"
                                control={<Radio />}
                                label="Compliance"
                              />
                              <FormControlLabel
                                value="Non Compliance"
                                control={<Radio />}
                                label="Non Compliance"
                              />
                            </RadioGroup>
                            {formData.compliance.nabl142 ===
                              "Non Compliance" && (
                              <TextField
                                fullWidth
                                label="Remarks for Non-Compliance"
                                value={formData.complianceRemarks.nabl142}
                                onChange={(e) =>
                                  handleComplianceRemarkChange(
                                    "nabl142",
                                    e.target.value,
                                  )
                                }
                                margin="normal"
                                multiline
                                rows={2}
                                required
                                placeholder="Please provide details for non-compliance..."
                              />
                            )}
                          </Grid>
                        </Grid>
                      </Card>

                      <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={3}>
                            <FormLabel sx={{ fontWeight: "bold" }}>
                              (c) NABL 163*
                            </FormLabel>
                          </Grid>
                          <Grid item xs={12} md={9}>
                            <RadioGroup
                              row
                              value={formData.compliance.nabl163}
                              onChange={(e) =>
                                handleComplianceChange(
                                  "nabl163",
                                  e.target.value,
                                )
                              }
                            >
                              <FormControlLabel
                                value="Compliance"
                                control={<Radio />}
                                label="Compliance"
                              />
                              <FormControlLabel
                                value="Non Compliance"
                                control={<Radio />}
                                label="Non Compliance"
                              />
                            </RadioGroup>
                            {formData.compliance.nabl163 ===
                              "Non Compliance" && (
                              <TextField
                                fullWidth
                                label="Remarks for Non-Compliance"
                                value={formData.complianceRemarks.nabl163}
                                onChange={(e) =>
                                  handleComplianceRemarkChange(
                                    "nabl163",
                                    e.target.value,
                                  )
                                }
                                margin="normal"
                                multiline
                                rows={2}
                                required
                                placeholder="Please provide details for non-compliance..."
                              />
                            )}
                          </Grid>
                        </Grid>
                      </Card>
                      {labTypeId === '3' && (
                        <>
                      <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={3}>
                            <FormLabel sx={{ fontWeight: "bold" }}>
                              (d) NABL 112A*
                            </FormLabel>
                          </Grid>
                          <Grid item xs={12} md={9}>
                            <RadioGroup
                              row
                              value={formData.compliance.nabl112a}
                              onChange={(e) =>
                                handleComplianceChange(
                                  "nabl112a",
                                  e.target.value,
                                )
                              }
                            >
                              <FormControlLabel
                                value="Compliance"
                                control={<Radio />}
                                label="Compliance"
                              />
                              <FormControlLabel
                                value="Non Compliance"
                                control={<Radio />}
                                label="Non Compliance"
                              />
                            </RadioGroup>
                            {formData.compliance.nabl112a ===
                              "Non Compliance" && (
                              <TextField
                                fullWidth
                                label="Remarks for Non-Compliance"
                                value={formData.complianceRemarks.nabl112a}
                                onChange={(e) =>
                                  handleComplianceRemarkChange(
                                    "nabl112a",
                                    e.target.value,
                                  )
                                }
                                margin="normal"
                                multiline
                                rows={2}
                                required
                                placeholder="Please provide details for non-compliance..."
                              />
                            )}
                          </Grid>
                        </Grid>
                      </Card>
                        <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={3}>
                              <FormLabel sx={{ fontWeight: "bold" }}>
                                (e) NABL 135* (Wherever applicable)
                              </FormLabel>
                          </Grid>
                          <Grid item xs={12} md={9}>
                            <RadioGroup
                              row
                              value={formData.compliance.nabl135}
                              onChange={(e) =>
                                handleComplianceChange(
                                  "nabl135",
                                  e.target.value,
                                )
                              }
                            >
                              <FormControlLabel
                                value="Compliance"
                                control={<Radio />}
                                label="Compliance"
                              />
                              <FormControlLabel
                                value="Non Compliance"
                                control={<Radio />}
                                label="Non Compliance"
                              />
                              <FormControlLabel
                                value="Not Applicable"
                                control={<Radio />}
                                label="Not Applicable"
                              />
                            </RadioGroup>
                            {formData.compliance.nabl135 ===
                              "Non Compliance" && (
                              <TextField
                                fullWidth
                                label="Remarks for Non-Compliance"
                                value={formData.complianceRemarks.nabl135}
                                onChange={(e) =>
                                  handleComplianceRemarkChange(
                                    "nabl135",
                                    e.target.value,
                                  )
                                }
                                margin="normal"
                                multiline
                                rows={2}
                                required
                                placeholder="Please provide details for non-compliance..."
                              />
                            )}
                          </Grid>
                        </Grid>
                      </Card>
                      </>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Quality Assurance Measures Section */}
              <Card
                sx={{
                  mb: 2,
                  boxShadow: 2,
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        color: "primary.main",
                        flex: 1,
                      }}
                    >
                      📊 {labTypeId === '2' ? '16.' : '18.'} Quality Assurance Measures
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={3}>
                    {/* Internal Audit */}
                    <Grid item xs={12}>
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ fontWeight: 600, mb: 2 }}
                        >
                          Internal Audit
                        </Typography>
                        {cabDetails?.InternalAuditFromDate ||
                        cabDetails?.InternalAuditToDate ? (
                          <Box
                            sx={{
                              mb: 2,
                              p: 2,
                              backgroundColor: "#f5f5f5",
                              borderRadius: 1,
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    color: "#1976d2",
                                    mb: 0.5,
                                  }}
                                >
                                  📅 Internal Audit Dates:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#333" }}
                                >
                                  {formatQualityAssuranceDate(
                                    cabDetails.InternalAuditFromDate,
                                    cabDetails.InternalAuditToDate,
                                  )}
                                </Typography>
                              </Box>
                              {cabDetails?.InternalAuditDocument && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<VisibilityIcon />}
                                  onClick={handleViewInternalAuditDocument}
                                  sx={{
                                    borderColor: "#1976d2",
                                    color: "#1976d2",
                                    "&:hover": {
                                      borderColor: "#1565c0",
                                      backgroundColor: "#e3f2fd",
                                    },
                                  }}
                                >
                                  View Document
                                </Button>
                              )}
                            </Box>
                          </Box>
                        ) : cabDetails?.InternalAuditDocument ? (
                          <Box
                            sx={{
                              mb: 2,
                              p: 2,
                              backgroundColor: "#f5f5f5",
                              borderRadius: 1,
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, color: "#1976d2" }}
                              >
                                📄 Internal Audit Document Available
                              </Typography>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={handleViewInternalAuditDocument}
                                sx={{
                                  borderColor: "#1976d2",
                                  color: "#1976d2",
                                  "&:hover": {
                                    borderColor: "#1565c0",
                                    backgroundColor: "#e3f2fd",
                                  },
                                }}
                              >
                                View Document
                              </Button>
                            </Box>
                          </Box>
                        ) : null}

                        {/* cl.8.8.3 Assessment Questions */}
                        <Box
                          sx={{
                            mt: 2,
                            p: 3,
                            backgroundColor: "#f8f9ff",
                            borderRadius: 2,
                            border: "1px solid #e3f2fd",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 2, color: "#1976d2" }}
                          >
                            {`${formData.standard} Clause 8.8.3`}
                          </Typography>
                          <Grid container spacing={3}>
                            {/* Trained Auditor */}
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 500 }}
                              >
                                Trained Auditor: as per {`${formData.standard}`}
                                <span style={{ color: "red" }}>*</span>
                              </Typography>
                              <FormControl component="fieldset">
                                <RadioGroup
                                  row
                                  value={formInputs.trainedAuditor}
                                  onChange={handleQAInputChange(
                                    "trainedAuditor",
                                  )}
                                >
                                  <FormControlLabel
                                    value="Yes"
                                    control={<Radio size="small" />}
                                    label="Yes"
                                  />
                                  <FormControlLabel
                                    value="No"
                                    control={<Radio size="small" />}
                                    label="No"
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Grid>

                            {/* Independence of Activity */}
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 500 }}
                              >
                                Independence of Activity being audited:{" "}
                                <span style={{ color: "red" }}>*</span>
                              </Typography>
                              <FormControl component="fieldset">
                                <RadioGroup
                                  row
                                  value={formInputs.independenceOfActivity}
                                  onChange={handleQAInputChange(
                                    "independenceOfActivity",
                                  )}
                                >
                                  <FormControlLabel
                                    value="Yes"
                                    control={<Radio size="small" />}
                                    label="Yes"
                                  />
                                  <FormControlLabel
                                    value="No"
                                    control={<Radio size="small" />}
                                    label="No"
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Grid>

                            {/* NCs Closed */}
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 500 }}
                              >
                                Status of the Non-Conformity raised during the
                                internal audit is closed or not?:{" "}
                                <span style={{ color: "red" }}>*</span>
                              </Typography>
                              <FormControl component="fieldset">
                                <RadioGroup
                                  row
                                  value={formInputs.ncsClosed}
                                  onChange={handleQAInputChange("ncsClosed")}
                                >
                                  <FormControlLabel
                                    value="Yes"
                                    control={<Radio size="small" />}
                                    label="Yes"
                                  />
                                  <FormControlLabel
                                    value="No"
                                    control={<Radio size="small" />}
                                    label="No"
                                  />
                                  <FormControlLabel
                                    value="NA"
                                    control={<Radio size="small" />}
                                    label="NA"
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Grid>

                            {/* Frequency of Internal Audit */}
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 500 }}
                              >
                                Frequency of Internal Audit:{" "}
                                <span style={{ color: "red" }}>*</span>
                              </Typography>
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                size="small"
                                value={formInputs.frequencyOfAudit}
                                onChange={handleQAInputChange(
                                  "frequencyOfAudit",
                                )}
                                placeholder="Enter frequency details..."
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, mt: 2, fontWeight: 500 }}
                              >
                                Comments:
                              </Typography>
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                size="small"
                                value={formInputs.auditComments}
                                onChange={handleQAInputChange("auditComments")}
                                placeholder="Enter comments..."
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Management Review */}
                    <Grid item xs={12}>
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ fontWeight: 600, mb: 2 }}
                        >
                          Management Review
                        </Typography>
                        {cabDetails?.ManagementReviewFromDate ||
                        cabDetails?.ManagementReviewToDate ? (
                          <Box
                            sx={{
                              mb: 2,
                              p: 2,
                              backgroundColor: "#f5f5f5",
                              borderRadius: 1,
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    color: "#2e7d32",
                                    mb: 0.5,
                                  }}
                                >
                                  📅 Management Review Dates:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#333" }}
                                >
                                  {formatQualityAssuranceDate(
                                    cabDetails.ManagementReviewFromDate,
                                    cabDetails.ManagementReviewToDate,
                                  )}
                                </Typography>
                              </Box>
                              {cabDetails?.ManagementReviewDocument && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<VisibilityIcon />}
                                  onClick={handleViewManagementReviewDocument}
                                  sx={{
                                    borderColor: "#2e7d32",
                                    color: "#2e7d32",
                                    "&:hover": {
                                      borderColor: "#1b5e20",
                                      backgroundColor: "#e8f5e8",
                                    },
                                  }}
                                >
                                  View Document
                                </Button>
                              )}
                            </Box>
                          </Box>
                        ) : cabDetails?.ManagementReviewDocument ? (
                          <Box
                            sx={{
                              mb: 2,
                              p: 2,
                              backgroundColor: "#f5f5f5",
                              borderRadius: 1,
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, color: "#2e7d32" }}
                              >
                                📄 Management Review Document Available
                              </Typography>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={handleViewManagementReviewDocument}
                                sx={{
                                  borderColor: "#2e7d32",
                                  color: "#2e7d32",
                                  "&:hover": {
                                    borderColor: "#1b5e20",
                                    backgroundColor: "#e8f5e8",
                                  },
                                }}
                              >
                                View Document
                              </Button>
                            </Box>
                          </Box>
                        ) : null}

                        {/* cl.8.9.1 Assessment Questions */}
                        <Box
                          sx={{
                            mt: 2,
                            p: 3,
                            backgroundColor: "#f0f8f0",
                            borderRadius: 2,
                            border: "1px solid #e8f5e8",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 2, color: "#2e7d32" }}
                          >
                            {`${formData.standard} Clause 8.9`}
                          </Typography>
                          <Grid container spacing={3}>
                            {/* All points of Standard covered */}
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 500 }}
                              >
                                All points of Standard covered:{" "}
                                <span style={{ color: "red" }}>*</span>
                              </Typography>
                              <FormControl component="fieldset">
                                <RadioGroup
                                  row
                                  value={formInputs.allPointsCovered}
                                  onChange={handleQAInputChange(
                                    "allPointsCovered",
                                  )}
                                >
                                  <FormControlLabel
                                    value="Yes"
                                    control={<Radio size="small" />}
                                    label="Yes"
                                  />
                                  <FormControlLabel
                                    value="No"
                                    control={<Radio size="small" />}
                                    label="No"
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Grid>

                            {/* Conducted within a year */}
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 500 }}
                              >
                                Conducted within a year:{" "}
                                <span style={{ color: "red" }}>*</span>
                              </Typography>
                              <FormControl component="fieldset">
                                <RadioGroup
                                  row
                                  value={formInputs.conductedWithinYear}
                                  onChange={handleQAInputChange(
                                    "conductedWithinYear",
                                  )}
                                >
                                  <FormControlLabel
                                    value="Yes"
                                    control={<Radio size="small" />}
                                    label="Yes"
                                  />
                                  <FormControlLabel
                                    value="No"
                                    control={<Radio size="small" />}
                                    label="No"
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, mt: 2, fontWeight: 500 }}
                              >
                                Comments:
                              </Typography>
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                size="small"
                                value={formInputs.managementComments}
                                onChange={handleQAInputChange(
                                  "managementComments",
                                )}
                                placeholder="Enter comments..."
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>
                    </Grid>

                    {/* PT Participation */}
                    <Grid item xs={12}>
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ fontWeight: 600, mb: 2 }}
                        >
                          PT Participation
                        </Typography>

                        {/* Availability of PT */}
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="body2"
                              sx={{ mb: 1, fontWeight: 500 }}
                            >
                              Availability of PT:{" "}
                              <span style={{ color: "red" }}>*</span>
                            </Typography>
                            <FormControl component="fieldset">
                              <RadioGroup
                                row
                                value={formInputs.ptAvailability}
                                onChange={handleQAInputChange("ptAvailability")}
                              >
                                <FormControlLabel
                                  value="Yes"
                                  control={<Radio size="small" />}
                                  label="Yes"
                                />
                                <FormControlLabel
                                  value="No"
                                  control={<Radio size="small" />}
                                  label="No"
                                />
                              </RadioGroup>
                            </FormControl>
                          </Grid>
                        </Grid>

                        {/* Reason if PT is not available */}
                        {formInputs.ptAvailability === "No" && (
                          <Box sx={{ mt: 2, mb: 3 }}>
                            <Typography
                              variant="body2"
                              sx={{ mb: 1, fontWeight: 500 }}
                            >
                              Reason for PT not being available:
                            </Typography>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              size="small"
                              value={formInputs.ptNotAvailableReason}
                              onChange={handleQAInputChange(
                                "ptNotAvailableReason",
                              )}
                              placeholder="Enter reason why PT is not available..."
                            />
                          </Box>
                        )}

                        {/* PT Participation Assessment */}
                        <Box
                          sx={{
                            p: 3,
                            backgroundColor: "#fff8e1",
                            borderRadius: 2,
                            border: "1px solid #ffecb3",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600, color: "#f57c00" }}
                            >
                              PT Participation
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={handleViewPTData}
                                sx={{
                                  borderColor: "#f57c00",
                                  color: "#f57c00",
                                  "&:hover": {
                                    borderColor: "#e65100",
                                    backgroundColor: "#fff3e0",
                                  },
                                }}
                              >
                                View PT Data
                              </Button>
                              {cabDetails?.QMSFile && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<DescriptionIcon />}
                                  onClick={handleViewQMSDocument}
                                  sx={{
                                    borderColor: "#1976d2",
                                    color: "#1976d2",
                                    "&:hover": {
                                      borderColor: "#1565c0",
                                      backgroundColor: "#e3f2fd",
                                    },
                                  }}
                                >
                                  View PT Document
                                </Button>
                              )}
                            </Box>
                          </Box>
                          <Grid container spacing={3}>
                            {/* Coverage of scope */}
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 500 }}
                              >
                                Coverage of scope:{" "}
                                <span style={{ color: "red" }}>*</span>
                              </Typography>
                              <FormControl component="fieldset">
                                <RadioGroup
                                  row
                                  value={formInputs.ptCoverageOfScope}
                                  onChange={handleQAInputChange(
                                    "ptCoverageOfScope",
                                  )}
                                >
                                  <FormControlLabel
                                    value="Adequate"
                                    control={<Radio size="small" />}
                                    label="Adequate"
                                  />
                                  <FormControlLabel
                                    value="Not Adequate"
                                    control={<Radio size="small" />}
                                    label="Not Adequate"
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Grid>

                            {/* Outlier */}
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 500 }}
                              >
                                Outlier: <span style={{ color: "red" }}>*</span>
                              </Typography>
                              <FormControl component="fieldset">
                                <RadioGroup
                                  row
                                  value={formInputs.ptOutlier}
                                  onChange={handleQAInputChange("ptOutlier")}
                                >
                                  <FormControlLabel
                                    value="Yes"
                                    control={<Radio size="small" />}
                                    label="Yes"
                                  />
                                  <FormControlLabel
                                    value="No"
                                    control={<Radio size="small" />}
                                    label="No"
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Grid>

                            {/* Root cause analysis remarks */}
                            {formInputs.ptOutlier === "Yes" && (
                              <Grid item xs={12}>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 1, fontWeight: 500 }}
                                >
                                  Remarks on root cause analysis:{" "}
                                  <span style={{ color: "red" }}>*</span>
                                </Typography>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={4}
                                  size="small"
                                  value={formInputs.ptOutlierRemarks}
                                  onChange={handleQAInputChange(
                                    "ptOutlierRemarks",
                                  )}
                                  placeholder="Enter root cause analysis remarks..."
                                />
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </Box>
                    </Grid>

                    {/* ILC Participation */}
                    <Grid item xs={12}>
                      <Box sx={{ mb: 3 }}>
                        <Box
                          sx={{
                            p: 3,
                            backgroundColor: "#f3e5f5",
                            borderRadius: 2,
                            border: "1px solid #e1bee7",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 3,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600, color: "#7b1fa2" }}
                            >
                              ILC Participation
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={handleViewILCData}
                              sx={{
                                borderColor: "#7b1fa2",
                                color: "#7b1fa2",
                                "&:hover": {
                                  borderColor: "#6a1b9a",
                                  backgroundColor: "rgba(123, 31, 162, 0.08)",
                                },
                              }}
                            >
                              View ILC Data
                            </Button>
                          </Box>
                          <Grid container spacing={3}>
                            {/* Coverage of scope through PT and/or ILC */}
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 500 }}
                              >
                                Coverage of scope through ILC:{" "}
                                <span style={{ color: "red" }}>*</span>
                              </Typography>
                              <FormControl component="fieldset">
                                <RadioGroup
                                  row
                                  value={formInputs.ilcCoverageOfScope}
                                  onChange={handleQAInputChange(
                                    "ilcCoverageOfScope",
                                  )}
                                >
                                  <FormControlLabel
                                    value="Adequate"
                                    control={<Radio size="small" />}
                                    label="Adequate"
                                  />
                                  <FormControlLabel
                                    value="Not Adequate"
                                    control={<Radio size="small" />}
                                    label="Not Adequate"
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Grid>

                            {/* Outlier */}
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 500 }}
                              >
                                Outlier: <span style={{ color: "red" }}>*</span>
                              </Typography>
                              <FormControl component="fieldset">
                                <RadioGroup
                                  row
                                  value={formInputs.ilcOutlier}
                                  onChange={handleQAInputChange("ilcOutlier")}
                                >
                                  <FormControlLabel
                                    value="Yes"
                                    control={<Radio size="small" />}
                                    label="Yes"
                                  />
                                  <FormControlLabel
                                    value="No"
                                    control={<Radio size="small" />}
                                    label="No"
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Grid>

                            {/* Root cause analysis remarks */}
                            {formInputs.ilcOutlier === "Yes" && (
                              <Grid item xs={12}>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 1, fontWeight: 500 }}
                                >
                                  Remarks on root cause analysis of the lab:{" "}
                                  <span style={{ color: "red" }}>*</span>
                                </Typography>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={4}
                                  size="small"
                                  value={formInputs.ilcOutlierRemarks}
                                  onChange={handleQAInputChange(
                                    "ilcOutlierRemarks",
                                  )}
                                  placeholder="Enter root cause analysis remarks..."
                                />
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Other Quality Assurance Measures */}
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Other Quality Assurance Measures
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          size="small"
                          value={formInputs.otherQualityMeasures}
                          onChange={handleQAInputChange("otherQualityMeasures")}
                          placeholder="Enter details about other quality assurance measures..."
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              {/* Overall recommendation of Assessment team */}
              <Card
                sx={{
                  mb: 2,
                  boxShadow: 2,
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        color: "primary.main",
                        flex: 1,
                      }}
                    >
                      📊 {labTypeId === '2' ? '17.' : '19.'} Overall recommendation of Assessment team
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    {/* <Grid item xs={12} md={6}>
                      <FormControl component="fieldset" fullWidth required>
                        <FormLabel
                          component="legend"
                          sx={{ fontWeight: "bold", mb: 2 }}
                        >
                          Is this lab Recommended for Accreditation?
                        </FormLabel>
                        <RadioGroup
                          row
                          value={formData.recommendForAccreditation}
                          onChange={(e) =>
                            handleInputChange(
                              "recommendForAccreditation",
                              e.target.value,
                            )
                          }
                        >
                          <FormControlLabel
                            value="Yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="No"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid> */}
                    {/* {formData.recommendForAccreditation === "Yes" && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Date for Submission of Corrective actions by the laboratory for Non Conformities raised."
                          type="date"
                          value={formData.correctiveActionsDate}
                          onChange={(e) =>
                            handleInputChange(
                              "correctiveActionsDate",
                              e.target.value,
                            )
                          }
                          margin="normal"
                          InputLabelProps={{ shrink: true }}
                          inputProps={{
                            min: new Date().toISOString().split("T")[0],
                            max: new Date(
                              new Date().setDate(new Date().getDate() + 30),
                            )
                              .toISOString()
                              .split("T")[0],
                          }}
                          required
                        />
                      </Grid>
                    )} */}
                    {/* <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Assessment Summary"
                        value={formData.assessmentSummary}
                        onChange={(e) =>
                          handleInputChange("assessmentSummary", e.target.value)
                        }
                        margin="normal"
                        multiline
                        rows={3}
                        required
                        placeholder="Provide detailed assessment summary"
                      />
                    </Grid> */}
                    {/* <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Recommendation of Assessment Team"
                        value={formData.recommendation}
                        onChange={(e) =>
                          handleInputChange("recommendation", e.target.value)
                        }
                        margin="normal"
                        multiline
                        rows={2}
                        required
                        placeholder="Enter assessment team recommendation"
                      />
                    </Grid> */}

                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal" required>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 1,
                            fontWeight: 500,
                            color: "rgba(0, 0, 0, 0.6)",
                          }}
                        >
                          Recommendation of Assessment Team{" "}
                          <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <Select
                          fullWidth
                          value={formData.recommendation}
                          onChange={(e) =>
                            handleInputChange("recommendation", e.target.value)
                          }
                          displayEmpty
                          sx={{ backgroundColor: "white" }}
                        >
                          <MenuItem value="" disabled>
                            <em>-- Select Recommendation --</em>
                          </MenuItem>
                          <MenuItem value="Accreditation may be granted / renewed / continued">
                            Accreditation may be granted / renewed / continued
                          </MenuItem>
                          <MenuItem value="Accreditation may be granted / renewed / continued subject to acceptance of corrective actions for the Non-Conformity (ies) raised.">
                            Accreditation may be granted / renewed / continued
                            subject to acceptance of corrective actions for the
                            Non-Conformity (ies) raised.
                          </MenuItem>
                          <MenuItem value="Denial of Accreditation">
                            Denial of Accreditation
                          </MenuItem>
                          <MenuItem value="Grant for the inclusion of authorized signatory">
                            Grant for the inclusion of authorized signatory
                          </MenuItem>
                          <MenuItem value="Grant of accreditation at the new premises with the existing / additional scope">
                            Grant of accreditation at the new premises with the
                            existing / additional scope
                          </MenuItem>
                          <MenuItem value="Accreditation may be granted for additional parameters">
                            Accreditation may be granted for additional
                            parameters
                          </MenuItem>
                          <MenuItem value="Accreditation may be granted for additional parameters subject to acceptance of corrective actions for the Non-Conformity (ies) raised.">
                            Accreditation may be granted for additional
                            parameters subject to acceptance of corrective
                            actions for the Non-Conformity (ies) raised.
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Number Of NCs Raised */}
              <Card
                sx={{
                  mb: 2,
                  boxShadow: 2,
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        color: "primary.main",
                        flex: 1,
                      }}
                    >
                      📊 {labTypeId === '2' ? '18.' : '20.'} NC Raised
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}
                      >
                        Total no. of non-conformities
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                              <TableCell sx={{ fontWeight: 600 }}>
                                Clause
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                Number of NC(s)
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>Clause 4</TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  type="number"
                                  value={formData.ncClause4 || 0}
                                  onChange={(e) =>
                                    handleInputChange(
                                      "ncClause4",
                                      e.target.value,
                                    )
                                  }
                                  inputProps={{ min: 0, readOnly: true }}
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Clause 5</TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  type="number"
                                  value={formData.ncClause5 || 0}
                                  onChange={(e) =>
                                    handleInputChange(
                                      "ncClause5",
                                      e.target.value,
                                    )
                                  }
                                  inputProps={{ min: 0, readOnly: true }}
                                  size="small"
                                  required
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Clause 6</TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  type="number"
                                  value={formData.ncClause6 || 0}
                                  onChange={(e) =>
                                    handleInputChange(
                                      "ncClause6",
                                      e.target.value,
                                    )
                                  }
                                  inputProps={{ min: 0, readOnly: true }}
                                  size="small"
                                  required
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Clause 7</TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  type="number"
                                  value={formData.ncClause7 || 0}
                                  onChange={(e) =>
                                    handleInputChange(
                                      "ncClause7",
                                      e.target.value,
                                    )
                                  }
                                  inputProps={{ min: 0, readOnly: true }}
                                  size="small"
                                  required
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Clause 8</TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  type="number"
                                  value={formData.ncClause8 || 0}
                                  onChange={(e) =>
                                    handleInputChange(
                                      "ncClause8",
                                      e.target.value,
                                    )
                                  }
                                  inputProps={{ min: 0, readOnly: true }}
                                  size="small"
                                  required
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow sx={{ backgroundColor: "#e3f2fd" }}>
                              <TableCell sx={{ fontWeight: 700 }}>
                                Total
                              </TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  type="number"
                                  value={
                                    (parseInt(formData.ncClause4) || 0) +
                                    (parseInt(formData.ncClause5) || 0) +
                                    (parseInt(formData.ncClause6) || 0) +
                                    (parseInt(formData.ncClause7) || 0) +
                                    (parseInt(formData.ncClause8) || 0)
                                  }
                                  InputProps={{
                                    readOnly: true,
                                  }}
                                  size="small"
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      fontWeight: 700,
                                    },
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="All NCs to be closed on/before"
                        type="date"
                        value={
                        formData.allNCsClosedDate ||
                        new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
                        }
                        onChange={(e) =>
                          handleInputChange("allNCsClosedDate", e.target.value)
                        }
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: new Date().toISOString().split("T")[0],
                          max: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
                        }}
                        required
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Save Button */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  mt: 3,
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading || dataLoading}
                  sx={{
                    px: 8,
                    py: 2,
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    borderRadius: 2,
                    boxShadow: 3,
                    background:
                      "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
                    "&:hover": {
                      boxShadow: 6,
                      background:
                        "linear-gradient(45deg, #764ba2 30%, #667eea 90%)",
                    },
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress
                        size={24}
                        sx={{ mr: 1 }}
                        color="inherit"
                      />
                      {naf6Id ? "Updating..." : "Saving..."}
                    </>
                  ) : (
                    <>{naf6Id ? "💾 Update NAF-6" : "💾 Save NAF-6"}</>
                  )}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Location Warning Dialog */}
        <LocationWarningDialog
          open={locationWarningOpen}
          onClose={() => setLocationWarningOpen(false)}
          distanceMessage={distanceMessage}
        />

        {/* Hourly Attendance Dialog */}
        <HourlyAttendanceDialog
          open={attendanceCheckDialogOpen}
          onClose={() => setAttendanceCheckDialogOpen(false)}
          message={attendanceCheckMessage}
          onRecheck={checkHourlyAttendance}
        />

        {/* Negative Option Confirmation Dialog */}
        <Dialog
          open={negativeOptionDialogOpen}
          onClose={() => {
            setNegativeOptionDialogOpen(false);
            setPendingNegativeSelection(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#d32f2f" }}>
              ⚠️ Non-Conformance Confirmation
            </Typography>
          </DialogTitle>
          <DialogContent>
            {/* Have Non Conformance raised in Checklist for following option()? */}
            {pendingNegativeSelection?.newlySelectedNegative &&
              pendingNegativeSelection.newlySelectedNegative.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    mb: 2,
                    p: 2,
                    backgroundColor: "#ffebee",
                    borderRadius: 1,
                    border: "1px solid #ef9a9a",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, mb: 1, color: "#d32f2f" }}
                  >
                    Selected Option:
                  </Typography>
                  {pendingNegativeSelection.newlySelectedNegative.length ===
                  1 ? (
                    <Typography variant="body2" sx={{ color: "#333", mt: 1 }}>
                      {pendingNegativeSelection.newlySelectedNegative[0]}
                    </Typography>
                  ) : (
                    <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                      {pendingNegativeSelection.newlySelectedNegative.map(
                        (option, idx) => (
                          <li key={idx} style={{ marginBottom: "4px" }}>
                            <Typography variant="body2" sx={{ color: "#333" }}>
                              {option}
                            </Typography>
                          </li>
                        ),
                      )}
                    </ul>
                  )}
                </Box>
              )}
            <Typography
              variant="body1"
              sx={{ mt: 2, fontWeight: 600, color: "#d32f2f" }}
            >
              Have you raised Non-Conformance in Checklist for the above option?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button
              onClick={() => {
                setNegativeOptionDialogOpen(false);
                setPendingNegativeSelection(null);
              }}
              variant="outlined"
              color="error"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // User confirmed - proceed with the selection
                if (pendingNegativeSelection) {
                  setQuestionAnswers({
                    ...questionAnswers,
                    [pendingNegativeSelection.questionKey]:
                      pendingNegativeSelection.selectedTexts,
                  });
                }
                setNegativeOptionDialogOpen(false);
                setPendingNegativeSelection(null);
              }}
              variant="contained"
              color="success"
              autoFocus
            >
              Yes, Confirmed
            </Button>
          </DialogActions>
        </Dialog>

        {/* PT Data Popup Dialog */}
        <Dialog
          open={ptDataPopupOpen}
          onClose={() => setPtDataPopupOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              PT Participation Data
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            {ptDataLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : ptData.length === 0 ? (
              <Alert severity="info">No PT participation data available.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell sx={{ fontWeight: 600 }}>Test/Group</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Discipline</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Participation In
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Frequency</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        PT Provider
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ptData.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.testParameter || "N/A"}</TableCell>
                        <TableCell>{item.discipline || "N/A"}</TableCell>
                        <TableCell>{item.participationIn || "N/A"}</TableCell>
                        <TableCell>{item.frequency || "N/A"}</TableCell>
                        <TableCell>
                          {item.participationIn === "PT" ||
                          item.participationIn === "PT/EQA (NABL Accredited)"
                            ? item.participationIn === "PT"
                              ? item.nodalLaboratory === "Others"
                                ? item.nodalLaboratoryOthers || "N/A"
                                : item.nodalLaboratory || "N/A"
                              : item.providerName || "N/A"
                            : item.participationIn === "PT/EQA (Others)"
                              ? item.nodalLaboratoryOthers || "N/A"
                              : "N/A"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPtDataPopupOpen(false)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* ILC Data Popup Dialog */}
        <Dialog
          open={ilcDataPopupOpen}
          onClose={() => setIlcDataPopupOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              ILC Participation Data
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            {ilcDataLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : ilcData.length === 0 ? (
              <Alert severity="info">
                No ILC participation data available.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell sx={{ fontWeight: 600 }}>Test/Group</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Discipline</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Participation In
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Frequency</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Nodal Lab</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ilcData.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.testParameter || "N/A"}</TableCell>
                        <TableCell>{item.discipline || "N/A"}</TableCell>
                        <TableCell>{item.participationIn || "N/A"}</TableCell>
                        <TableCell>{item.frequency || "N/A"}</TableCell>
                        <TableCell>
                          {item.nodalLaboratory === "Others"
                            ? item.nodalLaboratoryOthers || "N/A"
                            : item.nodalLaboratory || "N/A"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIlcDataPopupOpen(false)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
        {/* Assessor Details Dialog */}
        <Dialog
          open={assessorDialogOpen}
          onClose={() => setAssessorDialogOpen(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxHeight: "90vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#1976d2",
              color: "white",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            View Assessment Details
          </DialogTitle>
          <DialogContent sx={{ p: 0, mt: 2 }}>
            {appId && orgId ? (
              <AssessorDetailsView
                appId={encryptAppId(appId.toString())}
                orgId={encryptText(orgId.toString())}
                type="LAB"
                stage={cabDetails?.CurrentStage || ""}
                assessorsData={assessorsData}
              />
            ) : (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography>Loading...</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setAssessorDialogOpen(false)}
              variant="contained"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default NAF6FormNew;